////////////////////////////////////////////////////////////////////////////////
//
// The API access
//
////////////////////////////////////////////////////////////////////////////////

// {
//      "result" : "true|false", 	            // always present : request was successful?
//      "httpResponseCode" : "200|304|400….",	// if parameter ‘suppress_response_codes=true’ in request
//      "errorInfo" : {	    // If isOk=false
//          "developerMessage”:”Verbose, plain language description of the problem for the app developer with hints about how to fix it.",
//          "userMessage":"Pass this message on to the app user if needed.",
//          "errorCode" : 12345,
//          "moreInfo": // link to be defined
//      },
//      "data":{
//          // returned data object if present
//      },
//      "metadata": {
//          "numObjects" : "total number of objects that could be returned",
//          "offset" : "as set in request",
//          "limit" : "as set in request"
//      }
// }

import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import { TokenService } from "@/services/token.service";

import auth from "@/services/api/auth";
import account from "@/services/api/account";
import landlord from "@/services/api/landlord";
import app from "@/services/api/app";
import site from "@/services/api/site";
import kiosk from "@/services/api/kiosk";
import dispenser from "@/services/api/dispenser";
import device from "@/services/api/device";
import kioskManager from "@/services/api/kioskManager"
import entity from "@/services/api/entity";

import store from "@/store"
import {Mutation} from "@/store/mutations";
import router from "@/router";
import scheduleApi from '@/services/api/schedule'
import transcribe_pairs from "@/services/api/transcribe_pairs";

////////////////////////////////////////////////////////////////////////////////

// the placeholder below will be replaced with the real url by a shell scriot
//export const BASE_URL = 'https://@@PLATFORM_API_HOST@@';

export const BASE_URL = '/api';
export const BASE_SSE_URL = '/sse';
export const API_URL = `${BASE_URL}/ife/rest/v1`;
export const SSE_API_URL = `${BASE_SSE_URL}/ife/rest/v1`;
export const URL = `/ife/rest/v1`;
//export const API_URL_KIOSK_MGR = `${BASE_URL}/kmgr/rest/v1`;
export const API_URL_KIOSK_MGR = "https://traefik.infrafon.club/kmgr/rest/v1";

////////////////////////////////////////////////////////////////////////////////

const isSilent = (config={}) => {
    return Object.prototype.hasOwnProperty.call(config, 'silent') && config.silent
}
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};
const https = require('https');

// Create a dedicated axios instance for kiosk manager
const kioskManagerAxios = axios.create({
  httpsAgent: new https.Agent({ rejectUnauthorized: false }),
  baseURL: 'https://traefik.infrafon.club/kmgr/rest/v1'
});
const requestHandler = (request) => {
    if (!isSilent(request)) {
        store.commit(Mutation.loading, true)
    }
    return request
}

const errorHandler = (error) => {
  if (!isSilent(error)) {
    store.commit(Mutation.loading, false);
    
    if (error.response === undefined) {
      store.commit(Mutation.notify, { message: 'Network error' });
      return Promise.reject(new Error('Network error'));
    } else if (error.response.status === 401) {
      const originalRequest = error.config;
      
      // If this is already a refresh token request, don't try to refresh again
      if (originalRequest.url.includes('/refresh')) {
        store.commit(Mutation.auth.delete);
        router.push("/auth/login");
        return Promise.reject(new Error('Authentication error'));
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      isRefreshing = true;
      
      // Try to refresh the token
      return store.dispatch('auth/refreshToken').then(token => {
        // Update the authorization header
        ApiService.setHeader();
        
        // Process any queued requests
        processQueue(null, token);
        
        // Retry the original request
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return axios(originalRequest);
      }).catch(err => {
        processQueue(err, null);
        store.commit(Mutation.auth.delete);
        router.push("/auth/login");
        return Promise.reject(err);
      }).finally(() => {
        isRefreshing = false;
      });
    }
    
    // Preserve the original error structure
    return Promise.reject(error.response.data);
  }
  return Promise.reject(error);
}


const successHandler = (response) => {
    if (!isSilent(response)) {
        store.commit(Mutation.loading, false)
    }
    return response
}


axios.interceptors.request.use(
    request => requestHandler(request),
)
axios.interceptors.response.use(
    response => successHandler(response),
    error => errorHandler(error)
)

////////////////////////////////////////////////////////////////////////////////
//
// The 'T' stands for Tenant. getT uses the tentant API with the current set
// entity in global store
//
////////////////////////////////////////////////////////////////////////////////

const ApiService = {
    init() {
        Vue.use(VueAxios, axios);
        axios.defaults.baseURL = API_URL;
            axios.interceptors.request.use(
            config => {
                // Skip adding auth header for login and refresh requests
                if (config.url.includes('/login') || config.url.includes('/refresh')) {
                    return config;
                }
                
                const token = TokenService.token();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            error => {
                return Promise.reject(error);
            }
        );
    },

    setHeader() {
        axios.defaults.headers.common["Authorization"] = `Bearer ${TokenService.token()}`;
    },
    removeHeader() {
        axios.defaults.headers.common = {}
    },
    get(resource, config = {}) {
     //  //console.log(`[ApiService.get] Calling entity GEt ${axios.defaults.baseURL}/${resource, config.params}`);
               //console.log(config)
        return axios.get(`${resource}`, config)
    },
    getT(resource, config = {}) {
       //console.log(`[ApiService.get] Calling entity GEtT ${axios.defaults.baseURL}/${resource, config.params}`);

        return axios.get(`${this.tenantPrefix()}${resource}`, config)
    },
    post(resource, data) {
        return axios.post(`${resource}`, data)
    },
    postT(resource, data) {
        return axios.post(`${this.tenantPrefix()}${resource}`, data)
    },
    startEventSource(resource) {
    const url = `${API_URL}/${resource}`;
    return new EventSource(url);
    },

    postKioskManager(resource, data) {
        return axios({
            baseURL: '',
            url: `${API_URL_KIOSK_MGR}/kiosk/${store.getters.activeEntity}/${resource}`,
            method: 'post',
            data: data
        })
    },

    put(resource, params) {
        return axios.put(`${resource}`, params);
    },
    putT(resource, params) {
        return axios.put(`${this.tenantPrefix()}${resource}`, params);
    },
    delete(resource) {
        return axios.delete(resource)
    },
    deleteT(resource) {
        return axios.delete(`${this.tenantPrefix()}${resource}`)
    },
    deleteAllT(resource) {
    return axios.delete(`${this.tenantPrefix()}${resource}/all`)
},
   getDataview: (entityName, dataviewName) => ApiService.get(`/landlord/dataview/${entityName}/${dataviewName}`),
    // Add this method

    /**
     * Perform a custom Axios request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
     **/
    customRequest(data) {
        return axios(data)
    },

    /**
     * Returns the tenant prefix
     * @returns {string|string}
     */
    tenantPrefix() {
        const tenant = store.getters.activeEntity;
        return tenant ? `tenant/${tenant}/` : '';
    },


    // -- modules
    auth: auth,
    account: account,
    landlord: landlord,
   app: {
    ...app,
    all_all: (limit, offset) => ApiService.get('dataview', { params: { limit, offset } })
},
    site: site,
    kiosk: kiosk,
    entity: entity,
    dispenser: dispenser,
    device: device,
    transcribe_pairs: transcribe_pairs,
    kioskManager: kioskManager,
     schedule: scheduleApi,
}

////////////////////////////////////////////////////////////////////////////////

export default ApiService;